<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { handleSupportArticleAnchorClick } from '@/utils/supportArticleAnchor'

const props = withDefaults(defineProps<{
  html: string
  legacy?: boolean
}>(), {
  legacy: false
})

const hostRef = ref<HTMLElement | null>(null)
let articleRoot: ShadowRoot | null = null

const sharedStyles = `
  :host {
    display: block;
    width: 100%;
    -webkit-font-smoothing: antialiased;
    font-variant-ligatures: none;
    font-feature-settings: "liga" 0, "clig" 0;
    text-rendering: auto;
  }

  *, *::before, *::after {
    font-variant-ligatures: none;
    font-feature-settings: "liga" 0, "clig" 0;
    text-rendering: auto;
  }

  [id], a[name] {
    scroll-margin-top: 8rem;
  }

  img {
    max-width: 100%;
  }
`

const legacyStyles = `
  h1 { font-size: 20px; }
  h2, h3 { font-size: 15px; font-weight: normal; }
  h4 { font-size: 13px; font-weight: normal; }
  h5 { font-size: 16px; font-weight: normal; }
  p { line-height: 1.8; }
  a { color: #1877f2; }
`

/*
 * 处理文章 Shadow DOM 内的锚点点击
 * @param event - 文章内容点击事件
 */
const handleArticleClick = (event: Event) => {
  if (articleRoot) handleSupportArticleAnchorClick(event, articleRoot)
}

/*
 * 将后端文章内容渲染到 Shadow DOM
 * @description 隔离文章自带样式，避免影响页面导航、面包屑等外层元素
 */
const renderArticle = () => {
  if (!hostRef.value) return

  if (!articleRoot) {
    articleRoot = hostRef.value.attachShadow({ mode: 'open' })
    articleRoot.addEventListener('click', handleArticleClick)
  }

  articleRoot.innerHTML = `<style>${sharedStyles}${props.legacy ? legacyStyles : ''}</style>${props.html}`
  articleRoot.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'))
}

onMounted(renderArticle)

watch(
  () => [props.html, props.legacy],
  () => nextTick(renderArticle)
)

onBeforeUnmount(() => {
  articleRoot?.removeEventListener('click', handleArticleClick)
})
</script>

<template>
  <div ref="hostRef" class="support-article-content"></div>
</template>

<style lang="scss" scoped>
.support-article-content {
  display: block;
  width: 100%;
}
</style>
